import React from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import Masonry from "@mui/lab/Masonry";
import DashboardCustomizeOutlinedIcon from "@mui/icons-material/DashboardCustomizeOutlined";
import { i18n } from "@lingui/core";
import { t } from "@lingui/core/macro";
import { useTypedSelector } from "../../hooks.tsx";
import {
  getSelectedDashboard,
  getSelectedDashboardTileIds,
} from "../../store/conversation.tsx";
import DashboardTileCard from "./DashboardTileCard.tsx";
import DashboardTilePlaceholderCard from "./DashboardTilePlaceholderCard.tsx";

const DashboardView: React.FC = () => {
  const dashboard = useTypedSelector((state) => getSelectedDashboard(state));
  const tileIds = useTypedSelector((state) =>
    getSelectedDashboardTileIds(state),
  );
  const dashboardTilesById = useTypedSelector(
    (state) => state.conversationState.dashboardTilesById,
  );
  const dashboardTileTypesById = useTypedSelector(
    (state) => state.conversationState.dashboardTileTypesById,
  );
  const isLoading = useTypedSelector((state) =>
    dashboard
      ? state.conversationState.dashboardLoadingById[dashboard.id]
      : false,
  );

  if (!dashboard) {
    return null;
  }

  return (
    <Box sx={{ width: "100%", paddingBottom: 4 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          marginBottom: 2.5,
          px: { xs: 2, md: 2.5 },
          py: 1.75,
          borderRadius: 3,
          backgroundColor: "rgba(255,255,255,0.52)",
          border: "1px solid rgba(169,125,88,0.32)",
        }}
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: "999px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(169, 125, 88, 0.9)",
            flexShrink: 0,
          }}
        >
          <DashboardCustomizeOutlinedIcon
            sx={{ color: "rgba(47, 33, 24, 0.92)", fontSize: 18 }}
          />
        </Box>
        <Typography variant="h4">{dashboard.title}</Typography>
      </Box>

      {isLoading && tileIds.length === 0 ? (
        <Box
          sx={{
            minHeight: 240,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            color: "rgba(47, 33, 24, 0.78)",
          }}
        >
          <CircularProgress size={34} thickness={4.5} />
          <Typography variant="body1">
            {i18n._(t`Loading dashboard tiles...`)}
          </Typography>
        </Box>
      ) : tileIds.length === 0 ? (
        <Box
          sx={{
            borderRadius: 4,
            padding: 4,
            border: "1px dashed rgba(169,125,88,0.55)",
            background: "rgba(255,255,255,0.72)",
          }}
        >
          <Typography variant="body1">
            {i18n._(
              t`Save answers from your conversations to surface them here.`,
            )}
          </Typography>
        </Box>
      ) : (
        <Masonry columns={{ xs: 1, md: 2 }} spacing={2.25}>
          {tileIds.map((tileId) => {
            const tile = dashboardTilesById[tileId];
            if (!tile) {
              return (
                <DashboardTilePlaceholderCard
                  key={tileId}
                  type={dashboardTileTypesById[tileId]}
                />
              );
            }

            return <DashboardTileCard key={tileId} tile={tile} />;
          })}
        </Masonry>
      )}
    </Box>
  );
};

export default DashboardView;
